import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { TrendDataPoint } from '../../utils/indexedDB';

interface TrendChartProps {
  data: TrendDataPoint[];
  metric: 'revenue' | 'alerts' | 'services';
}

export function TrendChart({ data, metric }: TrendChartProps) {
  if (data.length === 0) {
    return (
      <div className="card p-6 text-center">
        <p className="text-slate-400">No historical data available yet</p>
        <p className="text-sm text-slate-500 mt-2">
          Upload reports to start tracking trends
        </p>
      </div>
    );
  }

  const config = {
    revenue: {
      dataKey: 'revenue',
      name: 'Revenue',
      color: '#22c55e',
      formatter: (value: number) => `$${value.toLocaleString()}`,
    },
    alerts: {
      dataKey: 'alerts',
      name: 'Total Alerts',
      color: '#f59e0b',
      formatter: (value: number) => value.toString(),
      secondaryKey: 'criticalAlerts',
      secondaryName: 'Critical',
      secondaryColor: '#ef4444',
    },
    services: {
      dataKey: 'serviceCount',
      name: 'Services',
      color: '#3b82f6',
      formatter: (value: number) => value.toLocaleString(),
    },
  };

  const cfg = config[metric];

  return (
    <div className="card p-4">
      <h3 className="text-sm font-medium text-slate-400 mb-4">
        {cfg.name} Trend
      </h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={{ stroke: '#475569' }}
            />
            <YAxis
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={{ stroke: '#475569' }}
              tickFormatter={metric === 'revenue' ? (v) => `$${(v / 1000).toFixed(0)}k` : undefined}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
              }}
              labelStyle={{ color: '#e2e8f0' }}
              formatter={(value: number, name: string) => [cfg.formatter(value), name]}
            />
            <Legend
              formatter={(value) => (
                <span className="text-slate-300 text-sm">{value}</span>
              )}
            />
            <Line
              type="monotone"
              dataKey={cfg.dataKey}
              name={cfg.name}
              stroke={cfg.color}
              strokeWidth={2}
              dot={{ fill: cfg.color, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            {metric === 'alerts' && (
              <Line
                type="monotone"
                dataKey="criticalAlerts"
                name="Critical"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: '#ef4444', strokeWidth: 2 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
